import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Grid,
  Typography,
  Button,
  Card,
  CardContent,
  TextField,
  List,
  ListItemButton,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Snackbar,
  Alert,
  LinearProgress,
  Chip,
  Divider,
  InputAdornment,
  CircularProgress,
  Tooltip,
  Paper,
  useTheme,
} from '@mui/material';

import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import AutoModeIcon from '@mui/icons-material/AutoMode';
import SendIcon from '@mui/icons-material/Send';
import BadgeIcon from '@mui/icons-material/Badge';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import BusinessIcon from '@mui/icons-material/Business';
import LocationOnIcon from '@mui/icons-material/LocationOn';

import api from '../../../../services/api';

export interface ADUser {
  title_id?: string;
  AD_title?: string;
  AD_company?: string;
  AD_givenName?: string;
  AD_sn?: string;
  AD_displayName?: string;
  AD_cn?: string;
  AD_mail?: string;
  AD_mobile?: string;
  AD_department?: string;
}

export interface SignatureFormState {
  name: string;
  position: string;
  mail: string;
  phone1: string;
  phone2: string;
  address: string;
  company: string;
}

export default function SignatureManagementPage() {
  const theme = useTheme();
  const darkMode = theme.palette.mode === 'dark';

  // Estados de dados
  const [users, setUsers] = useState<ADUser[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<ADUser[]>([]);
  const [selectedUser, setSelectedUser] = useState<ADUser | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Formulário editável (apenas para simulação/geração)
  const [formData, setFormData] = useState<SignatureFormState>({
    name: '',
    position: '',
    mail: '',
    phone1: '(31) 3399-4400',
    phone2: '',
    address: 'Sarzedo/MG',
    company: '',
  });

  // Estados de carregamento e preview
  const [loadingUsers, setLoadingUsers] = useState<boolean>(false);
  const [generatingSingle, setGeneratingSingle] = useState<boolean>(false);
  const [previewUrl, setPreviewUrl] = useState<string>('');

  // Estados do processamento em Lote (SSE)
  const [batchProcessing, setBatchProcessing] = useState<boolean>(false);
  const [batchProgress, setBatchProgress] = useState<{ current: number; total: number; message: string }>({
    current: 0,
    total: 0,
    message: '',
  });

  // Notificações (Snackbar)
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    type: 'success' | 'error' | 'warning' | 'info';
    message: string;
  }>({
    open: false,
    type: 'info',
    message: '',
  });

  const showSnackbar = (message: string, type: 'success' | 'error' | 'warning' | 'info' = 'info') => {
    setSnackbar({ open: true, type, message });
  };
  // 3. Selecionar usuário e preencher o formulário
  const handleSelectUser = (user: ADUser) => {
    setSelectedUser(user);

    const companyName = user.AD_company || '';
    const isFundicao = companyName.includes('Fundição');

    const mail = user.AD_mail || '';
    const fullName = `${user.AD_givenName || ''} ${user.AD_sn || ''}`.trim() || user.AD_cn || '';

    setFormData({
      name: fullName,
      position: user.AD_title || '',
      mail,
      phone1: isFundicao ? '(31) 3399-4401' : '(31) 3399-4400',
      phone2: user.AD_mobile || '',
      address: isFundicao ? 'Juatuba/MG' : 'Sarzedo/MG',
      company: companyName,
    });

    // Atualiza a URL da imagem de preview com cache-buster
    setPreviewUrl(`https://api.imic.com.br/static/images/signatures/${mail}.png?t=${Date.now()}`);
  };
  // 1. Carregar lista de usuários do AD
  // 1. Carregar lista de usuários do AD (com deduplicação)
  const fetchUsers = useCallback(async () => {
    setLoadingUsers(true);
    try {
      const response = await api.get('/it/querys/run', {
        params: {
          id: 18,
          token: 'fa89254808f6fe873defbab6714edaf1f9172321',
        },
      });

      const data: ADUser[] = Array.isArray(response?.data) ? response.data : [];

      // Filtra e-mails válidos
      const validUsers = data.filter(u => u.AD_mail && u.AD_mail.includes('@'));

      // Deduplica colaboradores pelo e-mail (garante lista única)
      const uniqueUsers = validUsers.reduce((acc: ADUser[], current) => {
        const mail = current.AD_mail?.trim().toLowerCase();
        if (mail && !acc.some(item => item.AD_mail?.trim().toLowerCase() === mail)) {
          acc.push(current);
        }
        return acc;
      }, []);

      setUsers(uniqueUsers);
      setFilteredUsers(uniqueUsers);

      if (uniqueUsers.length > 0 && !selectedUser) {
        handleSelectUser(uniqueUsers[0]);
      }
    } catch (error: any) {
      showSnackbar(error?.response?.data?.message || 'Erro ao carregar colaboradores do AD.', 'error');
    } finally {
      setLoadingUsers(false);
    }
  }, [selectedUser]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // 2. Filtrar lista pelo termo de busca
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredUsers(users);
      return;
    }
    const term = searchTerm.toLowerCase();
    const filtered = users.filter(
      u =>
        u.AD_givenName?.toLowerCase().includes(term) ||
        u.AD_sn?.toLowerCase().includes(term) ||
        u.AD_mail?.toLowerCase().includes(term) ||
        u.AD_title?.toLowerCase().includes(term) ||
        u.AD_company?.toLowerCase().includes(term),
    );
    setFilteredUsers(filtered);
  }, [searchTerm, users]);

  // 4. Gerar Assinatura Única (Dispara para a API)
  const handleGenerateSingle = async () => {
    if (!formData.mail) {
      showSnackbar('Selecione um e-mail válido.', 'warning');
      return;
    }

    setGeneratingSingle(true);
    try {
      const response = await api.post('/tools/signatures/generate', {
        token: process.env.REACT_APP_NODE_TOKEN || 'dGlAaW1pYy5jb20uYnI6SW5kJDk1Ng==',
        mail: formData.mail,
        name: formData.name,
        position: formData.position,
        phone1: formData.phone1,
        phone2: formData.phone2,
        address: formData.address,
        company: formData.company,
      });

      showSnackbar(response.data?.message || 'Assinatura gerada e aplicada com sucesso!', 'success');
      // Força a atualização do preview
      setPreviewUrl(`https://api.imic.com.br/static/images/signatures/${formData.mail}.png?t=${Date.now()}`);
    } catch (error: any) {
      showSnackbar(error?.response?.data?.message || 'Erro ao gerar assinatura.', 'error');
    } finally {
      setGeneratingSingle(false);
    }
  };

  // 5. Gerar Todas as Assinaturas em Lote (Streaming SSE via Fetch)
  const handleGenerateAllBatch = async () => {
    if (batchProcessing) return;

    setBatchProcessing(true);
    setBatchProgress({ current: 0, total: 0, message: 'Iniciando processamento em lote...' });

    try {
      const token = process.env.REACT_APP_NODE_TOKEN || 'dGlAaW1pYy5jb20uYnI6SW5kJDk1Ng==';
      const response = await fetch(`${api.defaults.baseURL}/tools/signatures/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'text/event-stream',
        },
        body: JSON.stringify({ token, mail: 'all' }),
      });

      if (!response.body) {
        throw new Error('Servidor não suporta resposta em stream.');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder('utf-8');

      // eslint-disable-next-line no-constant-condition
      while (true) {
        // eslint-disable-next-line no-await-in-loop
        const { done, value } = await reader.read();
        if (done) break;

        const textChunk = decoder.decode(value);
        const lines = textChunk.split('\n\n');

        // eslint-disable-next-line no-restricted-syntax
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.replace('data: ', '').trim());

              setBatchProgress(prev => ({
                current: data.current || prev.current,
                total: data.total || prev.total,
                message: data.message || prev.message,
              }));

              if (data.status === 'completed') {
                showSnackbar(data.message, 'success');
              }
            } catch (e) {
              // Ignora pequenas falhas de parse de chunks parciais
            }
          }
        }
      }
    } catch (error: any) {
      showSnackbar(error.message || 'Erro durante a geração em lote.', 'error');
    } finally {
      setBatchProcessing(false);
      if (formData.mail) {
        setPreviewUrl(`https://api.imic.com.br/static/images/signatures/${formData.mail}.png?t=${Date.now()}`);
      }
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      {/* CABEÇALHO DA PÁGINA */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
        <Box>
          <Typography variant="h5" fontWeight="bold" color="textPrimary">
            Gestão de Assinaturas de E-mail
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Gerencie, simule e aplique assinaturas corporativas no Exchange Online.
          </Typography>
        </Box>

        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<RefreshIcon />}
            onClick={fetchUsers}
            disabled={loadingUsers || batchProcessing}
          >
            Atualizar Lista
          </Button>

          <Button
            variant="contained"
            color="secondary"
            startIcon={batchProcessing ? <CircularProgress size={20} color="inherit" /> : <AutoModeIcon />}
            onClick={handleGenerateAllBatch}
            disabled={batchProcessing}
            disableElevation
          >
            {batchProcessing ? 'Processando Lote...' : 'Gerar Todas as Assinaturas'}
          </Button>
        </Box>
      </Box>

      {/* PAINEL DE PROGRESSO EM LOTE */}
      {batchProcessing && (
        <Paper sx={{ p: 2, mb: 3, borderLeft: `4px solid ${theme.palette.secondary.main}` }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
            <Typography variant="subtitle2" color="textPrimary">
              {batchProgress.message}
            </Typography>
            {batchProgress.total > 0 && (
              <Typography variant="caption" color="textSecondary" fontWeight="bold">
                {batchProgress.current} / {batchProgress.total} (
                {Math.round((batchProgress.current / batchProgress.total) * 100)}%)
              </Typography>
            )}
          </Box>
          <LinearProgress
            variant={batchProgress.total > 0 ? 'determinate' : 'indeterminate'}
            value={batchProgress.total > 0 ? (batchProgress.current / batchProgress.total) * 100 : 0}
            color="secondary"
            sx={{ height: 8, borderRadius: 4 }}
          />
        </Paper>
      )}

      {/* CONTEÚDO PRINCIPAL */}
      <Grid container spacing={3}>
        {/* COLUNA ESQUERDA: LISTA DE COLABORADORES */}
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box p={2}>
              <TextField
                fullWidth
                size="small"
                placeholder="Pesquisar por nome, e-mail ou cargo..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
              <Box mt={1} display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="caption" color="textSecondary">
                  Exibindo {filteredUsers.length} de {users.length} usuários
                </Typography>
              </Box>
            </Box>

            <Divider />

            <Box flexGrow={1} sx={{ overflowY: 'auto', maxHeight: 'calc(100vh - 320px)', minHeight: 400 }}>
              {loadingUsers ? (
                <Box display="flex" justifyContent="center" alignItems="center" p={4}>
                  <CircularProgress size={32} />
                </Box>
              ) : filteredUsers.length === 0 ? (
                <Box p={3} textAlign="center">
                  <Typography variant="body2" color="textSecondary">
                    Nenhum colaborador encontrado.
                  </Typography>
                </Box>
              ) : (
                <List disablePadding>
                  {filteredUsers.map((user, index) => {
                    const mail = user.AD_mail || '';
                    const isSelected = selectedUser?.AD_mail === mail;
                    const fullName = `${user.AD_givenName || ''} ${user.AD_sn || ''}`.trim() || user.AD_cn || mail;

                    return (
                      <React.Fragment key={`${mail}-${index}`}>
                        <ListItemButton
                          selected={isSelected}
                          onClick={() => handleSelectUser(user)}
                          sx={{
                            borderLeft: isSelected
                              ? `4px solid ${theme.palette.primary.main}`
                              : '4px solid transparent',
                          }}
                        >
                          <ListItemAvatar>
                            <Avatar
                              src={`https://api.imic.com.br/static/images/avatar/${mail}.jpg`}
                              alt={fullName}
                              sx={{ width: 40, height: 40 }}
                            >
                              {fullName.charAt(0)}
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            primary={
                              <Typography variant="body2" fontWeight={isSelected ? 'bold' : 'normal'} noWrap>
                                {fullName}
                              </Typography>
                            }
                            secondary={
                              <>
                                <Typography variant="caption" color="textSecondary" display="block" noWrap>
                                  {user.AD_title || 'Sem cargo'}
                                </Typography>
                                <Typography variant="caption" color="primary" noWrap>
                                  {mail}
                                </Typography>
                              </>
                            }
                          />
                        </ListItemButton>
                        <Divider component="li" />
                      </React.Fragment>
                    );
                  })}
                </List>
              )}
            </Box>
          </Card>
        </Grid>

        {/* COLUNA DIREITA: EDITOR E PRÉ-VISUALIZAÇÃO */}
        <Grid item xs={12} md={8}>
          <Grid container spacing={3}>
            {/* PAINEL DE EDIÇÃO / SIMULAÇÃO */}
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="subtitle1" fontWeight="bold">
                      Dados da Assinatura
                    </Typography>
                    <Chip label="Edição Temporária" size="small" color="warning" variant="outlined" />
                  </Box>

                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Nome Completo"
                        value={formData.name}
                        onChange={e => setFormData({ ...formData, name: e.target.value })}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <BadgeIcon fontSize="small" />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Cargo"
                        value={formData.position}
                        onChange={e => setFormData({ ...formData, position: e.target.value })}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <BusinessIcon fontSize="small" />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label="E-mail"
                        value={formData.mail}
                        onChange={e => setFormData({ ...formData, mail: e.target.value })}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <EmailIcon fontSize="small" />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Empresa / Unidade"
                        value={formData.company}
                        onChange={e => setFormData({ ...formData, company: e.target.value })}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <LocationOnIcon fontSize="small" />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Telefone Fixo"
                        value={formData.phone1}
                        onChange={e => setFormData({ ...formData, phone1: e.target.value })}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <PhoneIcon fontSize="small" />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Celular"
                        value={formData.phone2}
                        onChange={e => setFormData({ ...formData, phone2: e.target.value })}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <PhoneIcon fontSize="small" />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Cidade/UF"
                        value={formData.address}
                        onChange={e => setFormData({ ...formData, address: e.target.value })}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <LocationOnIcon fontSize="small" />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                  </Grid>

                  <Box display="flex" justifyContent="flex-end" mt={3}>
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={generatingSingle ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                      onClick={handleGenerateSingle}
                      disabled={generatingSingle || batchProcessing}
                      disableElevation
                    >
                      {generatingSingle ? 'Gerando...' : 'Gerar e Aplicar Esta Assinatura'}
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* PAINEL DE PRÉ-VISUALIZAÇÃO DA ASSINATURA */}
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="subtitle1" fontWeight="bold">
                      Pré-visualização da Assinatura Gerada
                    </Typography>
                    {previewUrl && (
                      <Tooltip title="Atualizar imagem">
                        <Button
                          size="small"
                          onClick={() =>
                            setPreviewUrl(
                              `https://api.imic.com.br/static/images/signatures/${formData.mail}.png?t=${Date.now()}`,
                            )
                          }
                        >
                          <RefreshIcon fontSize="small" />
                        </Button>
                      </Tooltip>
                    )}
                  </Box>

                  <Box
                    sx={{
                      p: 3,
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      minHeight: 220,
                      backgroundColor: darkMode ? 'rgba(255,255,255,0.05)' : '#f8f9fa',
                      borderRadius: 1,
                      border: '1px dashed rgba(0,0,0,0.15)',
                    }}
                  >
                    {formData.mail ? (
                      <Box textAlign="center">
                        <img
                          src={previewUrl}
                          alt={`Assinatura de ${formData.name}`}
                          style={{
                            maxWidth: '100%',
                            height: 'auto',
                            boxShadow: '0px 4px 12px rgba(0,0,0,0.1)',
                            borderRadius: 4,
                          }}
                          onError={(e: any) => {
                            e.target.style.display = 'none';
                            const errorBox = document.getElementById('preview-error');
                            if (errorBox) errorBox.style.display = 'block';
                          }}
                          onLoad={(e: any) => {
                            e.target.style.display = 'inline-block';
                            const errorBox = document.getElementById('preview-error');
                            if (errorBox) errorBox.style.display = 'none';
                          }}
                        />
                        <Box id="preview-error" style={{ display: 'none' }}>
                          <Typography variant="body2" color="textSecondary">
                            Nenhuma assinatura gerada ainda para <strong>{formData.mail}</strong>.
                          </Typography>
                          <Typography variant="caption" color="textSecondary" display="block" mt={0.5}>
                            Clique no botão "Gerar e Aplicar" acima para criar a primeira versão.
                          </Typography>
                        </Box>
                      </Box>
                    ) : (
                      <Typography variant="body2" color="textSecondary">
                        Selecione um colaborador na lista ao lado para visualizar a assinatura.
                      </Typography>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {/* SNACKBAR DE NOTIFICAÇÕES */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.type} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
